import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Newspaper, Clock, Warning } from "@phosphor-icons/react";
import AppShell from "@/components/AppShell";
import { api } from "@/lib/api";

function ImpactBadge({ impact }) {
  if (impact === "high") {
    return <span className="bg-red-500/20 text-red-400 px-2 py-1 rounded text-xs font-bold uppercase">HIGH RISK</span>;
  }
  if (impact === "medium") {
    return <span className="bg-orange-500/20 text-orange-400 px-2 py-1 rounded text-xs font-bold uppercase">MEDIUM RISK</span>;
  }
  return <span className="bg-green-500/20 text-green-400 px-2 py-1 rounded text-xs font-bold uppercase">LOW RISK</span>;
}

export default function NewsPage() {
  const { data, isLoading } = useQuery({
    queryKey: ["news_feed"],
    queryFn: () => api.get("/news", { params: { hours: 168 } }).then(r => r.data),
    refetchInterval: 30000,
  });

  const events = data?.events || [];
  
  // Separate past and upcoming
  const now = new Date();
  const pastEvents = events.filter(e => new Date(e.event_time) < now).reverse();
  const upcomingEvents = events.filter(e => new Date(e.event_time) >= now);

  return (
    <AppShell>
      <div className="p-6 max-w-5xl mx-auto space-y-6">
      <div>
        <h1 className="text-3xl font-bold flex items-center gap-3">
          <Newspaper className="text-blue-500" />
          News Risk Analysis
        </h1>
        <p className="text-zinc-400 mt-2">
          Economic events and their AI-predicted impact on XAUUSD and other correlated pairs.
        </p>
      </div>

      {isLoading ? (
        <div className="animate-pulse space-y-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-24 bg-zinc-800/50 rounded-lg"></div>
          ))}
        </div>
      ) : (
        <div className="space-y-8">
          
          <section>
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
              <Clock className="text-green-500" />
              Upcoming Events
            </h2>
            <div className="space-y-3">
              {upcomingEvents.length === 0 ? (
                <div className="text-zinc-500 italic">No upcoming events in this timeframe.</div>
              ) : upcomingEvents.map((ev) => (
                <NewsCard key={ev.event_id} event={ev} isUpcoming />
              ))}
            </div>
          </section>

          <section>
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2 text-zinc-500">
              <Clock />
              Past Events
            </h2>
            <div className="space-y-3 opacity-75">
              {pastEvents.length === 0 ? (
                <div className="text-zinc-500 italic">No past events.</div>
              ) : pastEvents.map((ev) => (
                <NewsCard key={ev.event_id} event={ev} />
              ))}
            </div>
          </section>

        </div>
      )}
      </div>
    </AppShell>
  );
}

function NewsCard({ event, isUpcoming }) {
  const t = new Date(event.event_time);
  
  // Basic prediction for display since "news information prediction" is requested
  const prediction = event.gold_impact 
    ? (event.forecast > event.previous ? "Bearish Gold" : "Bullish Gold") 
    : "Neutral";

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4 flex flex-col md:flex-row gap-4 justify-between items-start md:items-center">
      <div className="space-y-1">
        <div className="flex items-center gap-3">
          <ImpactBadge impact={event.impact} />
          <span className="font-mono text-sm bg-zinc-800 px-2 py-0.5 rounded text-zinc-300">
            {event.currency}
          </span>
          <span className="font-bold text-lg">{event.name}</span>
        </div>
        <div className="text-sm text-zinc-400 mt-2">
          {t.toLocaleString(undefined, { 
            weekday: 'short', month: 'short', day: 'numeric', 
            hour: '2-digit', minute: '2-digit' 
          })}
          {isUpcoming && event.starts_in_seconds > 0 && (
            <span className="ml-3 text-blue-400">
              Starts in {Math.round(event.starts_in_seconds / 3600)}h {Math.round((event.starts_in_seconds % 3600) / 60)}m
            </span>
          )}
        </div>
        {event.gold_impact && (
          <div className="mt-2 text-sm bg-blue-500/10 text-blue-300 px-3 py-1.5 rounded inline-block">
            <strong>AI Prediction:</strong> {prediction} based on forecast deviation.
          </div>
        )}
      </div>

      <div className="flex gap-6 text-sm">
        <div className="flex flex-col items-center">
          <span className="text-zinc-500 uppercase text-xs">Previous</span>
          <span className="font-mono">{event.previous || "—"}</span>
        </div>
        <div className="flex flex-col items-center">
          <span className="text-zinc-500 uppercase text-xs">Forecast</span>
          <span className="font-mono">{event.forecast || "—"}</span>
        </div>
        <div className="flex flex-col items-center">
          <span className="text-zinc-500 uppercase text-xs">Actual</span>
          <span className={`font-mono ${event.actual ? "text-white font-bold" : "text-zinc-600"}`}>
            {event.actual || "Pending"}
          </span>
        </div>
      </div>
    </div>
  );
}
